import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Image as ImageIcon, Search } from "lucide-react";
import { toast } from "sonner";

export default function MediaSeoManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${API}/seo/images`, { withCredentials: true });
      setImages(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch image assets");
    } finally {
      setLoading(false);
    }
  };

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/seo/images/extract`, {}, { withCredentials: true });
      toast.success(`Extracted ${res.data.extracted_count} new course thumbnails!`);
      fetchImages();
    } catch (err) {
      console.error(err);
      toast.error("Failed to extract images");
    } finally {
      setExtracting(false);
    }
  };

  const handleUpdate = async (image_url, alt_text, caption) => {
    try {
      await axios.post(
        `${API}/seo/images`,
        { image_url, alt_text, caption },
        { withCredentials: true }
      );
      toast.success("Image SEO saved!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save image SEO");
    }
  };

  const filteredImages = images.filter(img => 
    img.alt_text.toLowerCase().includes(searchTerm.toLowerCase()) || 
    img.image_url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Media SEO Library
          </h3>
          <p className="text-sm text-gray-500 dark:text-[#94A3B8]">Optimize your images for Google Search by adding Descriptive Alt Tags.</p>
        </div>
        <Button 
          onClick={handleExtract} 
          disabled={extracting}
          className="bg-white/10 text-gray-900 dark:text-white hover:bg-white/20 border border-gray-200 dark:border-white/20"
        >
          {extracting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          Auto-Extract Course Images
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-[#94A3B8]" />
        <Input 
          placeholder="Search by URL or Alt Text..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 bg-white dark:bg-[#0B101A] border-gray-200 dark:border-white/[0.1] text-gray-900 dark:text-white"
        />
      </div>

      {images.length === 0 ? (
        <div className="p-12 border border-gray-100 dark:border-white/[0.05] border-dashed rounded-xl text-center bg-white dark:bg-[#0B101A]">
          <ImageIcon className="w-12 h-12 text-gray-500 dark:text-[#94A3B8] mx-auto mb-3 opacity-50" />
          <h3 className="text-gray-900 dark:text-white font-medium mb-1">No Images Found</h3>
          <p className="text-gray-500 dark:text-[#94A3B8] text-sm mb-4">Click extract to automatically pull in all your course thumbnails.</p>
          <Button onClick={handleExtract} disabled={extracting} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl h-10 px-4">
            Extract Images
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredImages.map((img) => (
            <div key={img.image_id} className="bg-white dark:bg-[#0B101A] border border-gray-100 dark:border-white/[0.05] rounded-xl overflow-hidden group">
              <div className="h-40 bg-black/40 relative overflow-hidden flex items-center justify-center">
                <img 
                  src={img.image_url} 
                  alt={img.alt_text} 
                  className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.src = 'https://placehold.co/400x300/111/444?text=Broken+Image'; }}
                />
              </div>
              <div className="p-4 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 dark:text-[#94A3B8]">Alt Text (SEO)</Label>
                  <Input 
                    defaultValue={img.alt_text}
                    className="h-9 bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.1] text-gray-900 dark:text-white text-sm"
                    onBlur={(e) => {
                      if (e.target.value !== img.alt_text) {
                        handleUpdate(img.image_url, e.target.value, img.caption);
                        img.alt_text = e.target.value; // Optimistic update locally
                      }
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 dark:text-[#94A3B8]">Caption (Optional)</Label>
                  <Input 
                    defaultValue={img.caption || ""}
                    className="h-9 bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.1] text-gray-900 dark:text-white text-sm"
                    onBlur={(e) => {
                      if (e.target.value !== (img.caption || "")) {
                        handleUpdate(img.image_url, img.alt_text, e.target.value);
                        img.caption = e.target.value;
                      }
                    }}
                  />
                </div>
                <p className="text-[10px] text-gray-400 dark:text-[#52525B] truncate font-mono">
                  {img.image_url}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
